import React, { useEffect, useState, useContext } from 'react';

import { AlertContext } from "@context/AlertProvider";

// Material UI Components
import {
    Box,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogActions
} from '@mui/material';

import { formatUtcTimeToLocalHHmm} from "@utils/timeUtil"


import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';

import { dayNumberToDayName, monthNumberToMonthName } from "utils/timeUtil";
import { useSiteEventTimeRulesQuery } from "@query/TimeRuleQuery";

import { useTranslation } from "react-i18next";
import CreateTimeRuleModal from "@component/CreateTimeRuleModal";
import { useDeleteTimeRule } from "@mutation/timeRuleMutation";

function SiteTimeRuleList({ siteUuid, siteName }) {
    const [timeRules, setTimeRules] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null); // Track rule to delete
    const { t } = useTranslation(["main", "rewardedActivity"]);

    const siteEventTimeRulesQuery = useSiteEventTimeRulesQuery(siteUuid);
    const deleteTimeRule = useDeleteTimeRule();
    const { addAlertBanner } = useContext(AlertContext);

    useEffect(() => {
        if (siteEventTimeRulesQuery?.data?.data) {
            setTimeRules(siteEventTimeRulesQuery.data.data);
        }
    }, [siteEventTimeRulesQuery.data]);

    const formatTime = (timeStr) => {
        const splited = timeStr.split(":");
        return splited.slice(0, 2).join(":");
    };
    
    const renderOrDash = (items, formatterFn) => {
        if (!items || items.length === 0) return t("main:doesNotMatter");
        return items.map(formatterFn).join(", ");
    };

    const handleCreateNew = () => setModalOpen(true);

    const handleCreateNewModalClose = () => {
        siteEventTimeRulesQuery.refetch();
        setModalOpen(false);
    };

    const handleDeleteConfirmed = () => {
        if (!confirmDelete) return;
        deleteTimeRule.mutate(
            { data: { clubEventTimeRule_uuid: confirmDelete.uuid } },
            {
                onSuccess: () => {
                    addAlertBanner("Interval byl úspěšně smazán.", "success");
                    siteEventTimeRulesQuery.refetch();
                },
                onError: (error) => {
                    if (error.status === 403) {
                        addAlertBanner("Nemáte oprávnění smazat tento interval.", "error");
                    } else {
                        addAlertBanner("Nepodařilo se smazat interval.", "error");
                    }
                },
                onSettled: () => {
                    setConfirmDelete(null); // Close confirmation
                }
            }
        );
    };

    const columns = [
        {
            field: 'months',
            headerName: t("main:months"),
            flex: 1,
            minWidth: 150,
            sortable: false, filterable: false,
            renderCell: (params) => renderOrDash(params.value, monthNumberToMonthName),
        },
        {
            field: 'days_of_week',
            headerName: t("main:daysOfTheWeek"),
            flex: 1,
            minWidth: 150,
            sortable: false, filterable: false,
            renderCell: (params) => renderOrDash(params.value, dayNumberToDayName),
        },
        {
            field: 'time',
            headerName: t("main:timeOfTheDay"),
            flex: 1,
            minWidth: 150,
            sortable: false, filterable: false,
            renderCell: (params) => {
                const { start_time, end_time } = params.row;
                return `${formatUtcTimeToLocalHHmm(start_time)} – ${formatUtcTimeToLocalHHmm(end_time)}`;
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            type: 'actions',
            flex: 0.5,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
            sortable: false, filterable: false,
            renderCell: (params) => (
                <>
                    <IconButton
                        aria-label="delete"
                        size="small"
                        onClick={() => setConfirmDelete(params.row)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </>
            ),
        },
    ];

    const rows = timeRules.map(rule => ({ ...rule, id: rule.uuid }));

    return (
        <Box sx={{ width: '100%', display: "flex", gap: 2, px: 2, flexDirection: "column" }}>
            <Box sx={{
                px: 0, display: "flex", flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center"
            }}>
                <Typography variant="subtitle1"><b>{siteName}</b> - zvýhodněné časy </Typography>
                <Button
                    startIcon={<AddIcon />}
                    color="primary"
                    variant="contained"
                    onClick={handleCreateNew}
                >
                    {t("rewardedActivity:createNewTimeRule")}
                </Button>
            </Box>

            <DataGrid
                rows={rows}
                columns={columns}
                autoHeight
                disableRowSelectionOnClick
                hideFooter
                sx={{
                    border: '1px solid #e0e0e0',
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#f5f5f5',
                        borderBottom: '2px solid #ddd',
                    },
                    '& .MuiDataGrid-row:nth-of-type(odd)': {
                        backgroundColor: '#f9f9f9',
                    },
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: '#f0f0f0',
                    },
                    '& .MuiDataGrid-cell': {
                        borderBottom: 'none',
                    },
                }}
            />

            <CreateTimeRuleModal
                open={modalOpen}
                onClose={handleCreateNewModalClose}
                siteUuid={siteUuid}
            />

            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>{t("rewardedActivity:confirmDeleteMessage")}</DialogTitle>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDelete(null)}
                    >
                        {t("main:cancel") || "Zrušit"}
                    </Button>
                    <Button
                        color="error" // ✅ Red button
                        onClick={handleDeleteConfirmed}
                    >
                        {t("main:delete") || "Smazat"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SiteTimeRuleList;
